import { useEffect, useState } from "preact/hooks";

/**
 * On-device diagnostic overlay, gated by a `?debug` query param so it ships
 * inert. Built to debug the iOS digital-twin freeze (frames steady while
 * viewing the flock, then hitch/heat/crash a few seconds after a bird's audio
 * starts) — the working hypothesis is concurrent hardware media-decoder
 * saturation + WebContent OOM on the 3GB iPhone XR.
 *
 * It surfaces, with no Mac tether (iOS Safari exposes no JS memory API):
 *   - the build id, so you can confirm the deployed bundle is the latest build;
 *   - a live census of every <video>/<audio> decoder (paused/readyState/time +
 *     videoWidth, which is 0 until a video decoder is actually attached) — the
 *     direct test of "how many decoders are live the instant it freezes";
 *   - the Babylon twin fps (published to window.__twinFps by DigitalTwinView);
 *   - the focus count, to correlate the crash with the Nth bird.
 *
 * On `pagehide` it snapshots the readout to sessionStorage and re-renders it
 * after the auto-reload — so a WebContent OOM kill leaves its final state
 * readable on the next load (look for the "PREV (before reload)" block).
 */

const KEY = "__perfhud_last";

declare global {
  interface Window {
    __twinFps?: number;
    __perfFocusCount?: number;
    /** Story-audio lifecycle ring buffer (written by StoryPlayer.logAudio). */
    __storyAudioLog?: string[];
  }
}

/** True when the page was loaded with ?debug (any value). */
export function perfHudEnabled(): boolean {
  if (typeof window === "undefined") return false;
  return new URLSearchParams(window.location.search).has("debug");
}

function snapshot(): string[] {
  const out: string[] = [];
  out.push(`build ${typeof __BUILD_ID__ !== "undefined" ? __BUILD_ID__ : "?"}`);
  out.push(`fps ${window.__twinFps != null ? Math.round(window.__twinFps) : "?"}  focuses ${window.__perfFocusCount ?? 0}`);
  const media = Array.from(document.querySelectorAll("video,audio")) as HTMLMediaElement[];
  out.push(`decoders: ${media.length}`);
  media.forEach((m, i) => {
    const tag = m.tagName === "VIDEO" ? "V" : "A";
    const vw = (m as HTMLVideoElement).videoWidth ?? 0;
    const hasSrc = !!(m.currentSrc || m.getAttribute("src"));
    out.push(
      `${tag}${i} ${m.paused ? "paused" : "PLAY"} rs${m.readyState} ns${m.networkState} t${m.currentTime.toFixed(1)} ${tag === "V" ? (vw ? `dec${vw}` : "nodec") : hasSrc ? "src" : "empty"}`,
    );
  });
  const audioLog = window.__storyAudioLog;
  if (audioLog && audioLog.length) {
    out.push("— story audio —");
    // last 8 events, newest at the bottom
    for (const line of audioLog.slice(-8)) out.push(line);
  }
  return out;
}

export function PerfHud() {
  const [lines, setLines] = useState<string[]>([]);
  const [prev] = useState<string[]>(() => {
    try {
      const raw = sessionStorage.getItem(KEY);
      return raw ? (JSON.parse(raw) as string[]) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    const tick = () => setLines(snapshot());
    tick();
    const id = window.setInterval(tick, 500);
    // Persist the final readout so an OOM reload leaves the last state readable.
    const persist = () => {
      try {
        sessionStorage.setItem(KEY, JSON.stringify(snapshot()));
      } catch {
        /* ignore */
      }
    };
    window.addEventListener("pagehide", persist);
    window.addEventListener("visibilitychange", persist);
    return () => {
      window.clearInterval(id);
      window.removeEventListener("pagehide", persist);
      window.removeEventListener("visibilitychange", persist);
    };
  }, []);

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        zIndex: 2147483647,
        background: "rgba(0,0,0,0.72)",
        color: "#39ff6a",
        font: "10px/1.25 ui-monospace, Menlo, monospace",
        padding: "4px 6px",
        margin: 0,
        pointerEvents: "none",
        maxWidth: "72vw",
        whiteSpace: "pre",
        borderBottomRightRadius: "6px",
      }}
    >
      {lines.join("\n")}
      {prev.length > 0 && `\n— PREV (before reload) —\n${prev.join("\n")}`}
    </div>
  );
}
